/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, {
	useCallback,
	useMemo,
	useState,
	useEffect,
	type ComponentType,
	type FC,
	type SyntheticEvent,
} from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { jsx, cx, cssMap } from '@atlaskit/css';
// TODO: JFP-2824 | Suppressed to enable upgrade to ESLint v9 - please fix this if you can!
// eslint-disable-next-line jira/restricted/@atlaskit+analytics-next
import { AnalyticsContext } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import DropdownMenu, {
	DropdownItemCheckbox,
	DropdownItemCheckboxGroup,
} from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/utility/migration/chevron-down';
import Link from '@atlaskit/link';
import { Box, Inline } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import EmptyState from '@atlaskit/empty-state';
import useDebouncedCallback from '@atlassian/jira-platform-use-debounce/src/utils/use-debounce-callback/index.tsx';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { createAri } from '@atlassian/jira-platform-ari/src/index.tsx';
import {
	SCREEN,
	fireTrackAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import type { viewKBSpacesQuery } from '@atlassian/jira-relay/src/__generated__/viewKBSpacesQuery.graphql.ts';
import { KbContentContainer } from '@atlassian/jira-servicedesk-knowledge-common/src/common/ui/content-container/index.tsx';
import { KnowledgeBaseActionsGroup } from '@atlassian/jira-servicedesk-knowledge-common/src/common/ui/kb-action-group/index.tsx';
import { ConfluenceUpsellMessage } from '@atlassian/jira-servicedesk-knowledge-common/src/ui/confluence-upsell-message/index.tsx';
import type { PushHistory } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/knowledge-hub-push-provider/index.tsx';
import { VirtualServiceAgentTouchpoint } from '@atlassian/jira-servicedesk-virtual-agent-knowledge-base-touchpoint/src/ui/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { SearchField } from '@atlassian/jira-searchfield/src/index.tsx';
import searchNoResultsImage from '@atlassian/jira-servicedesk-common/src/utils/assets/no-search-results.svg';
import KnowledgeHubLoadingError from '@atlassian/jira-servicedesk-knowledge-hub/src/view/knowledge-hub-loading-error/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import PageHeader from '../common/page-header/view.tsx';
import { DraftArticleSuccessFlags } from '../common/draft-article-flags/view.tsx';
import commonMessages from '../common/i18n/messages.tsx';
import messages from '../knowledge-hub-browse-all-articles/messages.tsx';
import EmptyStateImage from '../common/assets/create-articles.svg';
import { TEAM_NAME, PACKAGE_NAME } from '../common/constants.tsx';
import { KnowledgeBaseHubArticlesTable } from './all-articles-table/index.tsx';

export type KnowledgeHubCrossSiteArticlesProps = {
	isAdmin: boolean;
	searchQuery: string;
	projectId: number;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	ReadyForUser: ComponentType<any>;
	projectKey: string;
};
export type InternalProps = KnowledgeHubCrossSiteArticlesProps & {
	push: PushHistory;
};
const KnowledgeHubCrossSiteAllArticles: FC<InternalProps> = ({ isAdmin, projectKey }) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const triggerText = useCallback(
		(spaces: Record<string, string>): string => {
			const { length } = Object.keys(spaces);
			let spaceName = '';
			if (length === 1) {
				const key = Object.keys(spaces)[0];
				spaceName = spaces[key].length > 15 ? `${spaces[key].substring(0, 15)}...` : spaces[key];
			}
			return formatMessage(messages.spacesDropdownText, {
				count: length,
				spaceName,
			});
		},
		[formatMessage],
	);

	const [searchQuery, setSearchQuery] = useState<string>('');
	const [selectedSpaces, setSelectedSpaces] = useState<Record<string, string>>({});
	const [dropdownText, setDropDownText] = useState(triggerText(selectedSpaces));
	const cloudId = useCloudId();
	const { data: projectContext } = useProjectContext();

	const container = useMemo(() => {
		return createAri({
			cloudId,
			resourceId: projectContext?.projectId?.toString() ?? '',
			resourceType: 'project',
			resourceOwner: 'jira',
		});
	}, [cloudId, projectContext?.projectId]);

	const knowledgeBaseMeta = useLazyLoadQuery<viewKBSpacesQuery>(
		graphql`
			query viewKBSpacesQuery($container: ID!, $cloudId: ID!) {
				knowledgeBase(cloudId: $cloudId) {
					knowledgeBase(container: $container) {
						__typename
						... on KnowledgeBaseSources {
							edge {
								node {
									name
									entityReference
								}
							}
						}
						... on QueryError {
							message
						}
					}
				}
			}
		`,
		{
			cloudId,
			container,
		},
	);

	useEffect(() => {
		if (knowledgeBaseMeta.knowledgeBase?.knowledgeBase?.__typename === 'QueryError') {
			fireErrorAnalytics({
				meta: {
					id: 'fetchCrossSiteSpaces',
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
				},
				attributes: {
					message: knowledgeBaseMeta.knowledgeBase.knowledgeBase.message,
				},
			});
		} else if (
			knowledgeBaseMeta.knowledgeBase?.knowledgeBase?.__typename === 'KnowledgeBaseSources'
		) {
			fireTrackAnalytics(createAnalyticsEvent({}), 'fetchCrossSiteSpaces succeeded');
		}
	}, [createAnalyticsEvent, knowledgeBaseMeta.knowledgeBase?.knowledgeBase]);

	const linkedSpaces = useMemo(() => {
		if (knowledgeBaseMeta.knowledgeBase?.knowledgeBase?.__typename !== 'KnowledgeBaseSources')
			return [];
		return knowledgeBaseMeta.knowledgeBase?.knowledgeBase?.edge
			.map((knowledgeSource) => {
				const node = knowledgeSource?.node;
				if (!node) return null;
				return {
					spaceName: node.name,
					id: node.entityReference,
				};
			})
			.filter(Boolean);
	}, [knowledgeBaseMeta]);

	const [debouncedCallback] = useDebouncedCallback((newValue) => {
		setSearchQuery(newValue);
	}, 300);

	const onSearch = useCallback(
		(value: string) => {
			debouncedCallback(value);
		},
		[debouncedCallback],
	);

	useMemo(() => {
		setDropDownText(triggerText(selectedSpaces));
	}, [selectedSpaces, triggerText]);

	const onSpacesToggled = useCallback((e: SyntheticEvent<HTMLElement>) => {
		const id = e.currentTarget.id;
		const spaceName = e.currentTarget.textContent;
		if (!spaceName || !id) return;
		setSelectedSpaces((spaces) => {
			if (id in spaces) {
				const newSpaces = { ...spaces };
				delete newSpaces[id];
				return newSpaces;
			}
			return { ...spaces, [id]: spaceName };
		});
	}, []);

	const noSearchResultsComponent = useMemo(() => {
		return (
			<EmptyState
				header={formatMessage(messages.noArticlesTitleMultipleSpaces)}
				imageUrl={searchNoResultsImage}
			/>
		);
	}, [formatMessage]);

	const emptyStateComponent = useMemo(() => {
		return (
			<EmptyState header={formatMessage(messages.noArticlesTitle)} imageUrl={EmptyStateImage} />
		);
	}, [formatMessage]);

	const DropdownItems = useMemo(() => {
		return linkedSpaces?.map((space, index) => (
			<DropdownItemCheckbox
				key={index}
				id={space.id}
				onClick={
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					onSpacesToggled as unknown as (
						e: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>,
					) => void
				}
				isSelected={space.id in selectedSpaces}
			>
				{space.spaceName}
			</DropdownItemCheckbox>
		));
	}, [linkedSpaces, onSpacesToggled, selectedSpaces]);

	const renderBottomBar = useMemo(() => {
		const settingsUrl = `/jira/servicedesk/projects/${projectKey}/settings/confluence-kb`;
		return (
			<Box xcss={cx(getWillShowNav4() ? styles.DescriptionNew : styles.DescriptionOld)}>
				<Inline alignBlock="start" spread="space-between" space="space.100">
					<Box>
						{getWillShowNav4() ? (
							<Box as="span">
								{formatMessage(messages.welcomeDescriptionCrossSiteArticlesTable)}
							</Box>
						) : (
							<Box as="p">{formatMessage(messages.welcomeDescriptionCrossSiteArticlesTable)}</Box>
						)}
						{isAdmin ? (
							<Link href={settingsUrl}>
								{formatMessage(messages.welcomeDescriptionCrossSiteArticlesTableLink)}
							</Link>
						) : null}
					</Box>
					{isAdmin && <VirtualServiceAgentTouchpoint />}
				</Inline>
			</Box>
		);
	}, [formatMessage, isAdmin, projectKey]);

	const renderSearchAndFilterContainer = useCallback(() => {
		return (
			<Box xcss={cx(getWillShowNav4() ? styles.SearchContainerNew : styles.SearchContainerOld)}>
				<Box xcss={cx(styles.TextFieldContainer)}>
					<SearchField
						placeholderAlwaysVisible
						shouldFitContainerWidth
						isAlwaysExpanded
						isAutocompleteDisabled
						isCompact
						placeholder={formatMessage(messages.searchPlaceholder)}
						value={searchQuery}
						onChange={onSearch}
						aria-describedby="hintText"
						aria-label={formatMessage(messages.searchLabelText)}
					/>
					<Box as="span" id="hintText" xcss={cx(styles.hiddenText)}>
						{formatMessage(messages.searchArticlesHintText)}
					</Box>
				</Box>
				{linkedSpaces?.length > 1 ? (
					<Box xcss={cx(styles.DropdownContainer)}>
						<DropdownMenu
							// eslint-disable-next-line jira/react-no-inline-function-prop
							trigger={({ triggerRef, ...props }) => (
								<Button {...props} ref={triggerRef} iconAfter={ChevronDownIcon}>
									{dropdownText}
								</Button>
							)}
						>
							<DropdownItemCheckboxGroup id="spaces-dropdown">
								{DropdownItems}
							</DropdownItemCheckboxGroup>
						</DropdownMenu>
					</Box>
				) : null}
			</Box>
		);
	}, [DropdownItems, dropdownText, formatMessage, linkedSpaces?.length, onSearch, searchQuery]);

	if (knowledgeBaseMeta.knowledgeBase?.knowledgeBase?.__typename === 'QueryError') {
		return <KnowledgeHubLoadingError projectKey={projectKey} enableHeader={false} />;
	}

	return (
		<UFOSegment name="knowledge-base-cross-site-articles">
			<AnalyticsContext
				data={{
					source: `knowledgeBrowseCrossSiteAllArticleSuccess${SCREEN}`,
				}}
			>
				<KbContentContainer>
					<Box xcss={cx(styles.confluenceUpsellContainerStyles)}>
						<ConfluenceUpsellMessage />
					</Box>

					<UFOSegment name="knowledge-base-cross-site-articles-headers">
						{getWillShowNav4() ? (
							renderBottomBar
						) : (
							<PageHeader
								// @ts-expect-error - TS2769 - No overload matches this call.
								truncateTitle
								bottomBar={renderBottomBar}
							>
								{formatMessage(commonMessages.articlesPageHeader)}
							</PageHeader>
						)}
						{getWillShowNav4() ? (
							<KnowledgeBaseActionsGroup>
								{renderSearchAndFilterContainer()}
							</KnowledgeBaseActionsGroup>
						) : (
							renderSearchAndFilterContainer()
						)}
					</UFOSegment>
					<KnowledgeBaseHubArticlesTable
						searchQuery={searchQuery}
						showSpaceColumn={linkedSpaces.length > 1}
						spaceAriList={Object.keys(selectedSpaces)}
						canViewSpaceUrl
						emptyStateComponent={emptyStateComponent}
						noSearchResultsComponent={noSearchResultsComponent}
						projectId={projectContext?.projectId?.toString()}
					/>
					<DraftArticleSuccessFlags />
				</KbContentContainer>
			</AnalyticsContext>
		</UFOSegment>
	);
};
export default KnowledgeHubCrossSiteAllArticles;
const styles = cssMap({
	DescriptionOld: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		maxWidth: `${gridSize * 96}px`,
	},
	DescriptionNew: {
		marginTop: token('space.200'),
	},
	hiddenText: {
		display: 'none',
	},
	IconWrapper: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		width: `${gridSize * 3}px`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		height: `${gridSize * 3}px`,
		display: 'flex',
		alignItems: 'center',
		cursor: 'pointer',
		color: token('color.text.subtle'),
		marginRight: token('space.050'),
	},
	confluenceUpsellContainerStyles: {
		marginTop: token('space.300'),
	},
	TextFieldContainer: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		width: `${gridSize * 30}px`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		height: `${gridSize * 4}px`,
	},
	SearchContainerNew: {
		display: 'flex',
	},
	SearchContainerOld: {
		display: 'flex',
		marginTop: token('space.300'),
		marginRight: 0,
		marginBottom: token('space.250'),
		marginLeft: 0,
	},
	DropdownContainer: {
		paddingTop: token('space.0'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.050'),
		marginLeft: token('space.100'),
	},
});
